import React, { useCallback, useMemo } from 'react';
import { Alert, Menu, MenuItemProps, Text } from '@televet/kibble-ui';
import partial from 'lodash-es/partial';
import {
  AutomationsFieldDescription,
  AutomationsFieldLabel,
  AutomationsFieldContainer,
  AutomationsSendConditionBlock,
} from 'pages/Automations/components/Layout';
import { AutomationTag, TagDisabledState } from '../../Layout/AutomationsTagBlock';
import {
  ConditionAttribute,
  ConditionOperator,
  SpeciesType,
  useGetAutomationSendConditionOptionsQuery,
} from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  updateAutomationErrors,
  updateAutomationSendConditions,
  addSendCondition,
  removeVisibleSendCondition,
  updateAutomationSendConditionErrors,
} from '../../../state/automationsSlice';
import {
  SendCondition,
  SendConditionAttribute,
  TaggableSendConditionAttribute,
} from '../../../types/AutomationSendConditions';
import useClinicUser from 'shared/hooks/useClinicUser';
import sortBy from 'lodash-es/sortBy';
import {
  TagBlocks,
  Tags,
  SendConditionItem,
  SendConditionErrorText,
} from 'pages/Automations/types/AutomationSendConditions';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { getVisibleSendConditions } from 'pages/Automations/state/utils/getVisibleSendConditions';
import { getSendConditionLabel } from 'pages/Automations/state/utils/getSendConditionLabel';
import { taggableSendConditionAttributes } from 'pages/Automations/constants/taggableSendConditionAttributes';
import { careMembershipStatusOptions } from 'pages/Automations/types/SendConditionCareMembershipStatus';

export const allSendConditions: SendConditionItem[] = [
  {
    value: ConditionAttribute.AppointmentTypeId,
    label: getSendConditionLabel(ConditionAttribute.AppointmentTypeId),
  },
  {
    value: ConditionAttribute.ClinicEmployeeId,
    label: getSendConditionLabel(ConditionAttribute.ClinicEmployeeId),
  },
  { value: ConditionAttribute.ClinicRoomId, label: getSendConditionLabel(ConditionAttribute.ClinicRoomId) },
  { value: ConditionAttribute.SpeciesType, label: getSendConditionLabel(ConditionAttribute.SpeciesType) },
  { value: ConditionAttribute.PetAgeInYears, label: getSendConditionLabel(ConditionAttribute.PetAgeInYears) },
  {
    value: ConditionAttribute.CareMembershipStatus,
    label: getSendConditionLabel(ConditionAttribute.CareMembershipStatus),
  },
];

const invalidGlobalSendConditions = [
  ConditionAttribute.AppointmentTypeId,
  ConditionAttribute.ClinicEmployeeId,
  ConditionAttribute.ClinicRoomId,
];

export const SendConditions = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const dispatch = useAppDispatch();
  const sendConditions = useAppSelector((state) => state.automations.automationDraft.sendConditions);
  const visibleSendConditions = useMemo(
    () =>
      getVisibleSendConditions(sendConditions).sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [sendConditions],
  );
  const containerErrors = useAppSelector((state) => state.automations.errors.sendConditionContainer);
  const fieldErrors = useAppSelector((state) => state.automations.errors.sendConditions);
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);
  const containerErrorText = useGetFieldErrorText(containerErrors);
  const { isFeatureEnabled } = useFeatureFlag();
  const hasAutomationsPatientAgeSendCondition = isFeatureEnabled(FeatureFlagName.AutomationsPatientAgeSendCondition);
  const isCareEnabled = isFeatureEnabled(FeatureFlagName.CarePlans);

  const containerHasError = useMemo(() => {
    return Boolean(containerErrors.length);
  }, [containerErrors]);

  const availableSendConditions = useMemo(() => {
    return allSendConditions.filter(({ value }) => {
      return (
        (value !== ConditionAttribute.PetAgeInYears || hasAutomationsPatientAgeSendCondition) &&
        (value !== ConditionAttribute.CareMembershipStatus || isCareEnabled)
      );
    });
  }, [hasAutomationsPatientAgeSendCondition, isCareEnabled]);

  const sendConditionMenuOptions: SendConditionItem[] = useMemo(() => {
    const menuitems = availableSendConditions.filter((availableSendCondition) => {
      let isInvalidGlobalCondition = false;

      if (isGlobalEditor) {
        if (invalidGlobalSendConditions.includes(availableSendCondition.value)) {
          isInvalidGlobalCondition = true;
        }
      }

      const isAvailable = !visibleSendConditions.some((visibleSendCondition) => {
        return availableSendCondition.value === visibleSendCondition.attribute;
      });
      const isPatientAgeAvailable = !(
        availableSendCondition.value === ConditionAttribute.PetAgeInYears &&
        !isFeatureEnabled(FeatureFlagName.AutomationsPatientAgeSendCondition)
      );
      return isAvailable && isPatientAgeAvailable && !isInvalidGlobalCondition;
    });
    return menuitems;
  }, [availableSendConditions, isGlobalEditor, visibleSendConditions, isFeatureEnabled]);

  const handleAddCondition = useCallback(
    (selectedConditionValue: SendConditionAttribute) => {
      dispatch(addSendCondition(selectedConditionValue));

      if (containerHasError) {
        dispatch(updateAutomationErrors({ sendConditionContainer: [] }));
      }
    },
    [dispatch, containerHasError],
  );

  const handleDeleteCondition = useCallback(
    (selectedConditionValue: SendConditionAttribute) => {
      dispatch(removeVisibleSendCondition(selectedConditionValue));
      if (fieldErrors[selectedConditionValue]) {
        dispatch(updateAutomationSendConditionErrors({ conditionAttribute: selectedConditionValue, errors: [] }));
      }
    },
    [fieldErrors, dispatch],
  );

  const { data: sendConditionOptionsData } = useGetAutomationSendConditionOptionsQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
  });

  const allTagBlocks = useMemo(() => {
    const tagBlocks: TagBlocks = {
      [ConditionAttribute.AppointmentTypeId]:
        sendConditionOptionsData?.appointmentTypes?.map(({ id, name, displayName, isVisible }) => ({
          label: name || displayName || '',
          value: id,
          isSelected: sendConditions[ConditionAttribute.AppointmentTypeId]?.operand.value.includes(id),
          isActive: !!isVisible,
          isDeleted: false,
        })) || [],
      [ConditionAttribute.ClinicRoomId]:
        sendConditionOptionsData?.clinicRooms?.map(({ id, name, isActive, isVisible, isDeleted }) => ({
          label: name || '',
          value: id,
          isSelected: sendConditions[ConditionAttribute.ClinicRoomId]?.operand.value.includes(id),
          isActive: !!isActive && !!isVisible,
          isDeleted: !!isDeleted,
        })) || [],
      [ConditionAttribute.ClinicEmployeeId]:
        sendConditionOptionsData?.clinicEmployees?.map(
          ({ id, firstName, lastName, isActive, isDeleted, isVisible }) => ({
            label: `${firstName} ${lastName}` || '',
            value: id,
            isSelected: sendConditions[ConditionAttribute.ClinicEmployeeId]?.operand.value.includes(id),
            isActive: !!isActive && !!isVisible,
            isDeleted: !!isDeleted,
          }),
        ) || [],
      [ConditionAttribute.SpeciesType]:
        Object.values(SpeciesType)?.map((speciesType) => ({
          label: speciesType,
          value: speciesType,
          isSelected: sendConditions[ConditionAttribute.SpeciesType]?.operand.value.includes(speciesType),
          isActive: true,
          isDeleted: false,
        })) || [],
      [ConditionAttribute.CareMembershipStatus]:
        careMembershipStatusOptions.map(({ label, value }) => ({
          label,
          value,
          isSelected: sendConditions[ConditionAttribute.CareMembershipStatus]?.operand.value.includes(value),
          isActive: true,
          isDeleted: false,
        })) || [],
    };
    return tagBlocks;
  }, [sendConditionOptionsData, sendConditions]);

  const tagBlockMenuOptions = useMemo(() => {
    return Object.keys(taggableSendConditionAttributes).reduce((prev, attribute) => {
      let menuItems: MenuItemProps[] = [];
      switch (attribute) {
        case ConditionAttribute.AppointmentTypeId:
          menuItems = sortBy(
            allTagBlocks[attribute].filter(({ isActive }) => isActive),
            ['label'],
          );
          break;
        case ConditionAttribute.ClinicRoomId:
        case ConditionAttribute.ClinicEmployeeId:
        case ConditionAttribute.SpeciesType:
        case ConditionAttribute.CareMembershipStatus:
          menuItems = sortBy(
            allTagBlocks[attribute].filter(({ isActive, isDeleted }) => isActive && !isDeleted),
            ['label'],
          );
          break;
        default:
          menuItems = [];
      }
      prev[attribute as TaggableSendConditionAttribute] = menuItems;
      return prev;
    }, {} as Record<TaggableSendConditionAttribute, MenuItemProps[]>);
  }, [allTagBlocks]);

  const selectedConditionTags: Record<TaggableSendConditionAttribute, AutomationTag[]> = useMemo(() => {
    const getSelectedTags = (options: Tags[]): AutomationTag[] => {
      const selectedOptions = options
        .filter(({ isSelected }) => isSelected)
        .map(({ label, value, isActive, isDeleted }) => ({
          label: `${label || ''}${
            isDeleted ? ` (${TagDisabledState.Deleted})` : !isActive ? ` (${TagDisabledState.Inactive})` : ''
          }`,
          value: value || '',
        }));

      return sortBy(selectedOptions, ['label']);
    };
    return {
      [ConditionAttribute.AppointmentTypeId]: getSelectedTags(allTagBlocks[ConditionAttribute.AppointmentTypeId]),
      [ConditionAttribute.ClinicRoomId]: getSelectedTags(allTagBlocks[ConditionAttribute.ClinicRoomId]),
      [ConditionAttribute.ClinicEmployeeId]: getSelectedTags(allTagBlocks[ConditionAttribute.ClinicEmployeeId]),
      [ConditionAttribute.SpeciesType]: getSelectedTags(allTagBlocks[ConditionAttribute.SpeciesType]),
      [ConditionAttribute.CareMembershipStatus]: getSelectedTags(allTagBlocks[ConditionAttribute.CareMembershipStatus]),
    };
  }, [allTagBlocks]);

  const handleUpdateCondition = useCallback(
    (updates: Partial<SendCondition> & { attribute: SendConditionAttribute }) => {
      dispatch(
        updateAutomationSendConditions({
          [updates.attribute as SendConditionAttribute]: {
            ...sendConditions[updates.attribute as SendConditionAttribute],
            ...updates,
          },
        }),
      );
    },
    [sendConditions, dispatch],
  );

  const handleRemoveTag = useCallback(
    (conditionAttribute: TaggableSendConditionAttribute, tag: string) => {
      const selectedTags = sendConditions[conditionAttribute]?.operand.value;
      const updatedTags = (selectedTags ?? []).filter((value) => value !== tag);
      dispatch(
        updateAutomationSendConditions({
          [conditionAttribute]: { attribute: conditionAttribute, operand: { value: updatedTags } },
        }),
      );
    },
    [sendConditions, dispatch],
  );

  const handleAddTag = useCallback(
    ({
      conditionAttribute,
      allSelectedItems,
    }: {
      conditionAttribute: TaggableSendConditionAttribute;
      allSelectedItems: MenuItemProps[] | undefined;
    }) => {
      if (allSelectedItems) {
        const ids = allSelectedItems.map(({ value }) => value || '');
        dispatch(
          updateAutomationSendConditions({
            [conditionAttribute]: {
              attribute: conditionAttribute,
              operator: ConditionOperator.In,
              operand: { value: ids },
            },
          }),
        );

        if (fieldErrors[conditionAttribute]) {
          dispatch(updateAutomationSendConditionErrors({ conditionAttribute: conditionAttribute, errors: [] }));
        }
      }
    },
    [dispatch, fieldErrors],
  );

  const handleClearAllTags = useCallback(
    (conditionAttribute: TaggableSendConditionAttribute) => {
      dispatch(
        updateAutomationSendConditions({
          [conditionAttribute]: { attribute: conditionAttribute, operand: { value: [] } },
        }),
      );
    },
    [dispatch],
  );

  return (
    <AutomationsFieldContainer>
      <AutomationsFieldLabel>Send Conditions</AutomationsFieldLabel>
      <AutomationsFieldDescription>
        This automation will send for appointments with the following criteria:
      </AutomationsFieldDescription>
      {visibleSendConditions.map((sendCondition) => {
        // TODO: Preserve order of conditions when user adds in different order than availableSendConditions
        // TODO: Account for previously selected tags that have since been deleted
        const { attribute } = sendCondition;
        return (
          <AutomationsSendConditionBlock
            key={attribute}
            sendCondition={sendCondition}
            onDeleteCondition={partial(handleDeleteCondition, attribute)}
            tags={selectedConditionTags[attribute as TaggableSendConditionAttribute] ?? []}
            onRemoveTag={handleRemoveTag}
            onClearAllTags={handleClearAllTags}
            onUpdateCondition={handleUpdateCondition}
            hasError={Boolean(fieldErrors[attribute].length)}
            errorText={SendConditionErrorText[attribute]}
            canBeDeleted
            listProps={{
              isMultiSelect: true,
              isSearchable: true,
              canSelectAll: true,
              onSelectAll: (selectedItems): void =>
                handleAddTag({
                  conditionAttribute: attribute as TaggableSendConditionAttribute,
                  allSelectedItems: selectedItems,
                }),
              onSelect: (selectedItem, allSelectedItems): void =>
                handleAddTag({ conditionAttribute: attribute as TaggableSendConditionAttribute, allSelectedItems }),
              menuItems: tagBlockMenuOptions[attribute as TaggableSendConditionAttribute] ?? [],
            }}
          />
        );
      })}
      {!visibleSendConditions.length && (
        <Alert
          title="At least one send condition is required to activate this automation"
          status="info"
          hideCloseButton
          mb={2}
        />
      )}
      {containerHasError && (
        <Text mt={2} size="xs" variant="danger">
          {containerErrorText}
        </Text>
      )}
      {Boolean(sendConditionMenuOptions.length) && (
        <Menu
          buttonProps={{
            leftIconName: 'plus',
            text: 'Add Condition',
            variant: 'ghost',
            showRightIcon: false,
            size: 'md',
          }}
          listProps={{
            menuItems: sendConditionMenuOptions,
            onSelect: (selectedItem): void => handleAddCondition(selectedItem.value as SendConditionAttribute),
          }}
        />
      )}
    </AutomationsFieldContainer>
  );
};
